import { useCallback } from 'react'
import { useConfigStore } from '../store/configStore'
import type { ConfigNode } from '../types/config.types'

export function useConfigTree() {
  const tree = useConfigStore((s) => s.tree)
  const selectedIds = useConfigStore((s) => s.selectedIds)
  const expandedIds = useConfigStore((s) => s.expandedIds)
  const searchQuery = useConfigStore((s) => s.searchQuery)
  const setSelectedIds = useConfigStore((s) => s.setSelectedIds)
  const setExpandedIds = useConfigStore((s) => s.setExpandedIds)
  const toggleExpanded = useConfigStore((s) => s.toggleExpanded)
  const setSearchQuery = useConfigStore((s) => s.setSearchQuery)

  const onSelect = useCallback(
    (ids: string[]) => setSelectedIds(ids),
    [setSelectedIds]
  )
  const onExpand = useCallback(
    (ids: string[]) => setExpandedIds(ids),
    [setExpandedIds]
  )

  return {
    nodes: tree,
    selectedIds,
    expandedIds,
    searchQuery,
    onSelect,
    onExpand,
    toggleExpanded,
    setSearchQuery,
  }
}

export function flattenTree(nodes: ConfigNode[]): ConfigNode[] {
  const out: ConfigNode[] = []
  function walk(list: ConfigNode[]) {
    for (const n of list) {
      out.push(n)
      if (n.children?.length) walk(n.children)
    }
  }
  walk(nodes)
  return out
}

export function findNodeById(nodes: ConfigNode[], id: string): ConfigNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    if (n.children) {
      const found = findNodeById(n.children, id)
      if (found) return found
    }
  }
  return null
}

/** Parent of node with given id (owner catalog/document for objectAttribute/objectTabularSection). */
export function findParentNode(nodes: ConfigNode[], nodeId: string): ConfigNode | null {
  for (const n of nodes) {
    if (n.children?.some((c) => c.id === nodeId)) return n
    if (n.children) {
      const found = findParentNode(n.children, nodeId)
      if (found) return found
    }
  }
  return null
}

export function getPathToNode(nodes: ConfigNode[], targetId: string): string[] {
  const path: string[] = []
  function walk(list: ConfigNode[]): boolean {
    for (const n of list) {
      path.push(n.id)
      if (n.id === targetId) return true
      if (n.children && walk(n.children)) return true
      path.pop()
    }
    return false
  }
  walk(nodes)
  return path
}
